import React, { useState } from 'react';
import { Contact } from '../types/Contact';
import { CsvImport, GoogleImport } from './imports';

interface ImportsManagementProps {
    contacts?: Contact[];
    onContactsUpdate?: () => void;
    onDuplicatesFound?: () => void;
    onPendingContactsFound?: () => void;
    user?: any;
}

export default function ImportsManagement({ contacts = [], onContactsUpdate, onDuplicatesFound, onPendingContactsFound, user }: ImportsManagementProps) {
    const [activeTab, setActiveTab] = useState<'csv' | 'google'>('csv');

    return (
        <div className="min-h-screen bg-gray-900 text-white">
            <div className="max-w-4xl mx-auto p-6">
                <div className="bg-gray-800 rounded-lg shadow-lg p-6">
                    {/* Header */}
                    <div className="border-b border-gray-700 pb-6 mb-6">
                        <h2 className="text-2xl font-bold text-white mb-2">Import Contacts</h2>
                        <p className="text-gray-300">
                            Import contacts from various sources to build your network vault.
                        </p>
                    </div>

                    {/* Tab Navigation */}
                    <div className="flex space-x-1 mb-6">
                        <button
                            onClick={() => setActiveTab('csv')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'csv'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                }`}
                        >
                            CSV Import
                        </button>
                        <button
                            onClick={() => setActiveTab('google')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'google'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                }`}
                        >
                            Google Contacts
                        </button>
                    </div>

                    {/* Tab Content */}
                    <div className="space-y-6">
                        {activeTab === 'csv' && (
                            <CsvImport
                                onContactsUpdate={onContactsUpdate}
                                onDuplicatesFound={onDuplicatesFound}
                                onPendingContactsFound={onPendingContactsFound}
                            />
                        )}

                        {activeTab === 'google' && (
                            <GoogleImport onContactsUpdate={onContactsUpdate} />
                        )}
                    </div>

                    {/* Current Stats */}
                    {contacts.length > 0 && (
                        <div className="mt-8 pt-6 border-t border-gray-700">
                            <h3 className="text-lg font-semibold text-white mb-3">Current Vault Status</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-gray-700 p-4 rounded-lg">
                                    <div className="text-2xl font-bold text-blue-400">{contacts.length}</div>
                                    <div className="text-sm text-gray-300">Total Contacts</div>
                                </div>
                                <div className="bg-gray-700 p-4 rounded-lg">
                                    <div className="text-2xl font-bold text-green-400">
                                        {contacts.filter(c => c.tier !== 'Unassigned').length}
                                    </div>
                                    <div className="text-sm text-gray-300">Tiered Contacts</div>
                                </div>
                                <div className="bg-gray-700 p-4 rounded-lg">
                                    <div className="text-2xl font-bold text-yellow-400">
                                        {contacts.filter(c => c.is_priority).length}
                                    </div>
                                    <div className="text-sm text-gray-300">Priority Contacts</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
